import { JobContacts } from './job-contacts.interface';
import { JobCustomFields } from './job-custom-fields.interface';
import { JobTimeSheets } from './job-timesheet.interface';
import { JobForms } from './job-form.interface';
import { JobEngineers } from './job-engineer.interface';
import { JobExpenses } from './job-expenses.interface';
import { JobNotes } from './job-notes.interface';
import { JobResources } from './job-resources.interface';
import { JobParts } from './job-parts.interface';
import { JobChildRelatedEntities, JobParentRelatedEntities } from './job-related-entities.interface';
import { JobTasks } from './job-task.interface';
import { JobInvoiceLines } from './job-invoice-line.interface';
import { JobOrderLines } from './job-order-line.interface';
import { JobReturnLines } from './job-return-line.interface';
import { Customer } from './customer.interface';
import { Site } from './site.interface';
import { Location } from './location.interface';
import { SubLocation } from './sub-location.interface';
import { Contract } from './contract.interface';
import { SLA } from './sla.interface';
import { Engineer } from './engineer.interface';
import { Team } from './team.interface';
import { JobStatus } from './job-status.interface';
import { JobType } from './job-type.interface';
import { JobArea } from './job-area.interface';
import { JobCategory } from './job-category.interface';
import { JobIssue } from './job-issue.interface';
import { JobTaskAssets } from './job-assets.interface';
import { JobTaskAssetForms } from "./job-asset-form.interface";
import { JobTaskAssetParts } from "./job-task-asset-parts.interface";

export enum DurationUnit {
  Minute = 'minute',
  Hour = 'hour',
  Day = 'day',
  Month = 'month',
}

export interface JobAddressInfo {
  address: string;
  address1: string;
  address2: string;
  address3: string;
  tel: string;
  email: string;
  postCode: string;
}

export interface JobGeography {
  latitude: number | null;
  longitude: number | null;
  geoTagDateTime: Date | null;
}

export interface JobCRM {
  slaId: string;
  contractId: string;
  contractName: string;
  siteId: string;
  siteName: string;
  siteContactName: string;
  siteContactTel: string;
  siteContactEmail: string;
  locationId: string | null;
  subLocationId: string | null;
  crmContactId: string | null;
  customer: Customer;
  site: Site;
  location: Location;
  subLocation: SubLocation;
  contract: Contract;
  sla: SLA;
}

export interface JobSignature {
  signatureName: string;
  signatureDateTime: Date | null;
  internalSignatureBlobUri: string;
  signatureBlobUri: string;
  internalSignatureName: string;
  internalSignatureDateTime: Date | null;
}

export interface JobSchedule {
  startDate: string;
  endDate: string;
  duration: number | null;
  durationUnit: DurationUnit;
}

export interface JobAssignment {
  engineerId: string | null;
  pdaUserId: string;
  teamId: string | null;
  teamName: string;
  oldTeamId: string | null;
  oldTeamName: string;
  engineer: Engineer;
  team: Team;
}

export interface JobStatusAccept {
  acceptUser: string;
  acceptUserName: string;
  acceptDate: Date | null;
}

export interface JobStatusComplete {
  completeUser: string;
  completeUserName: string;
  completeDate: Date | null;
  completionNotes: string;
}

export interface JobStatusArrival {
  arrivalUser: string;
  arrivalUserName: string;
  arrivalDate: Date | null;
}

export interface JobStatusReject {
  rejectUser: string;
  rejectUserName: string;
  rejectDate: Date | null;
  rejectNotes: string;
  jobRejectReasonId: string | null;
  rejectBlobUri: string;
}

export interface JobSiteOperative {
  siteOperativeId: string | null;
  completeSiteOperativeId: string | null;
  rejectSiteOperativeId: string | null;
  arrivalSiteOperativeId: string | null;
  acceptSiteOperativeId: string | null;
  createdBySiteOperativeId: string | null;
}

export interface JobRecurrence {
  recurrenceRule: string;
  recurrenceParent: string | null;
  recurrenceException: string;
  exceptionDates: string[];
}

export interface JobCore {
  jobId: string;
  jobTypeId: number;
  createDate: string;
  serverCreateDate: Date | null;
  serverModifyDate: Date | null;
  clientPO: string;
  clientRefNo: string;
  submissionNotes: string;
  officeNotes: string;
  priority: number | null;
  priorityName: string;
  lastAuditInterval: number | null;
  notify: boolean | null;
  notifyInterval: number | null;
  jobAreaId: string | null;
  jobCategoryId: string | null;
  jobIssueId: string | null;
  blobUploadCompleteDateTime: Date | null;
  quoteId: string | null;
  countryId: string | null;
  countryName: string;
  cloneFromId: string | null;
  jobStatusId: string;
  createdByPdaUserId: string;
  createPdaUserName: string;
  createWebUserName: string;
  webUserId: string | null;
  jobStatus: JobStatus;
  jobType: JobType;
  jobArea: JobArea;
  jobCategory: JobCategory;
  jobIssue: JobIssue;
  isEventSchema: boolean;
}

export interface JobDetailsForm {
  clientRefNo: string;
  clientPO: string;
  submissionNotes: string;
  officeNotes: string;
  completionNotes: string;
}

export interface Job extends
  JobCore,
  JobAddressInfo,
  JobGeography,
  JobCRM,
  JobSignature,
  JobSchedule,
  JobAssignment,
  JobStatusAccept,
  JobStatusComplete,
  JobStatusArrival,
  JobStatusReject,
  JobSiteOperative,
  JobRecurrence,
  JobContacts,
  JobTimeSheets,
  JobForms,
  JobEngineers,
  JobExpenses,
  JobCustomFields,
  JobNotes,
  JobResources,
  JobParts,
  JobParentRelatedEntities,
  JobChildRelatedEntities,
  JobTasks,
  JobInvoiceLines,
  JobOrderLines,
  JobReturnLines,
  JobTaskAssets,
  JobTaskAssetParts,
  JobTaskAssetForms
{}
